import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from "@mui/material";
import { keyframes } from "@mui/system"; // Better import for keyframes in MUI v5
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteIcon from "@mui/icons-material/Delete";

// Animation keyframes
const bounceIn = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const shake = keyframes`
  0% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  50% { transform: translateX(5px); }
  75% { transform: translateX(-5px); }
  100% { transform: translateX(0); }
`;

const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(-10px); }
  100% { opacity: 1; transform: translateY(0); }
`;

export const CommonDialog = ({ dialog, onClose }) => {
  // Destructure for cleaner code and default values
  const {
    isOpen = false,
    type = "simple",
    subtype,
    title,
    message,
    buttonText = "OK",
    cancelText = "Cancel",
    onConfirm,
    disableEscapeKeyDown = false,
  } = dialog;

  const isSpecialDialog = type === "success" || type === "fail";
  const isDelete = type === "delete" || subtype === "delete";

  // Get color based on type
  const getColor = () => {
    switch (type) {
      case "success":
        return "success.main";
      case "error":
      case "fail":
      case "delete":
        return "error.main";
      case "simple":
        return subtype === "delete" ? "warning.main" : "primary.main";
      default:
        return "primary.main";
    }
  };

  // Get icon based on type
  const getIcon = () => {
    const baseStyle = { fontSize: 64, color: getColor() };

    if (type === "success") {
      return (
        <CheckCircleOutlineIcon
          sx={{ ...baseStyle, animation: `${bounceIn} 0.6s ease-out` }}
        />
      );
    }
    if (type === "fail" || type === "error") {
      return (
        <ErrorOutlineIcon
          sx={{ ...baseStyle, animation: `${shake} 0.5s ease-in-out` }}
        />
      );
    }
    if (isDelete) {
      return (
        <DeleteIcon
          sx={{ ...baseStyle, animation: `${bounceIn} 0.6s ease-out` }}
        />
      );
    }
    return <InfoOutlinedIcon sx={baseStyle} />;
  };

  // Handle purely closing the dialog (Cancel, Backdrop click, Escape key)
  const handleDismiss = (event, reason) => {
    // Prevent backdrop click from closing if disableEscapeKeyDown is true
    if (reason === "backdropClick" && disableEscapeKeyDown) return;
    onClose();
  };

  // Handle actual confirmation (OK/Confirm button click)
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleDismiss} // Attach dismiss here, NOT confirm
      disableEscapeKeyDown={disableEscapeKeyDown}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      PaperProps={{
        sx: {
          borderRadius: 3,
          minWidth: "350px",
          animation: `${fadeIn} 0.3s ease-out`,
          ...(isSpecialDialog && {
            textAlign: "center",
            padding: 4,
          }),
        },
      }}
    >
      {/* Special Dialogs (Success/Fail) */}
      {isSpecialDialog ? (
        <>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            {getIcon()}
          </Box>
          <DialogTitle
            id="dialog-title"
            sx={{ color: getColor(), fontSize: "1.5rem", fontWeight: "bold" }}
          >
            {title}
          </DialogTitle>
          <DialogContent
            id="dialog-description"
            sx={{ typography: "body1", px: 3 }}
          >
            {message}
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pt: 2, pb: 1 }}>
            <Button
              onClick={handleConfirm}
              variant="contained"
              autoFocus
              sx={{
                backgroundColor: getColor(),
                color: "white",
                px: 4,
                py: 1,
                borderRadius: 2,
                "&:hover": { backgroundColor: getColor(), opacity: 0.9 },
              }}
            >
              {buttonText}
            </Button>
          </DialogActions>
        </>
      ) : (
        /* Standard Confirmation Dialogs */
        <>
          <DialogTitle id="dialog-title" sx={{ color: getColor() }}>
            {title}
          </DialogTitle>
          <DialogContent id="dialog-description">
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
              {getIcon()}
              <Box sx={{ typography: "body1" }}>{message}</Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ pt: 1 }}>
            {/* Removed autoFocus here to prevent dual-focus conflicts */}
            <Button onClick={handleDismiss}>{cancelText}</Button>

            <Button
              onClick={handleConfirm}
              variant={isDelete ? "contained" : "text"}
              autoFocus // Focus only on the primary action
              aria-label={dialog.buttonLabel || "Confirm dialog action"}
              sx={{
                color: isDelete ? "white" : getColor(),
                backgroundColor: isDelete ? getColor() : "transparent",
                borderRadius: 2,
                "&:hover": {
                  backgroundColor: isDelete ? getColor() : `${getColor()}10`,
                  opacity: 0.9,
                },
              }}
            >
              {buttonText}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
